import {
  getGasMarketDifferenceChart,
  getGasMarketDifferenceComparisonTable,
  getGasMarketDifferenceDaily,
} from "$/api/market-monitoring/gas-analysis.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import LineGraph from "$/lib/components/graphs/LineGraph";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import GraphDateRangeSelector from "$/lib/components/molecules/GraphDateRangeSelector";
import TableGraphWidget from "$/lib/components/widgets/TableGraphWidget";
import { RenderTableNumberCell } from "$/lib/components/widgets/TableGraphWidget/RenderTableNumberCell";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { MarketDifferenceTableType } from "$/lib/types/api/market-monitoring/gas-analysis.types";
import type { ColumnType } from "$/lib/types/Graphs.types";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import type { FutureProductPeriod } from "$/lib/types/models/FutureProduct.types";
import {
  format,
  getDate,
  getLast6MonthsStartAndEndDates,
} from "$/lib/utils/functions/date.functions";
import { handleOnChangeDefaultGraphsTabs } from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import {
  cn,
  getArrayKeys,
  getMinMax,
} from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

const COLORS = ["#273c7b", "#82b8b2", "#64a34b", "#d45359", "#f49ba7"];

type Props = {
  selectedDate: Date;
} & CustomDashboardProps;

const GasMarketDifference = ({
  selectedDate,
  id,
  isDraggable = false,
  isShowcase = false,
}: Props) => {
  const [selectedGraphTabId, setSelectedGraphTabId] = useState("LAST_6_MONTHS");

  const { start, end } = getLast6MonthsStartAndEndDates();

  const [dateRange, setDateRange] = useState<[Date, Date]>([start, end]);

  const [tablePeriod, setTablePeriod] = useState<FutureProductPeriod | "DAY">(
    "QUARTER",
  );

  const { data, isLoading } = useQuery({
    queryKey: ["get-gas-market-difference-chart", dateRange],
    queryFn: () =>
      getGasMarketDifferenceChart({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
      }),
  });

  const { data: tableData } = useQuery({
    queryKey: [
      "get-gas-market-difference-comparison-table",
      dateRange,
      tablePeriod,
    ],
    queryFn: () =>
      getGasMarketDifferenceComparisonTable({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
        period: tablePeriod,
      }),
  });

  const { data: daily } = useQuery({
    queryKey: ["get-gas-market-difference-daily", selectedDate],
    queryFn: () => getGasMarketDifferenceDaily({ date: getDate(selectedDate) }),
  });

  const toDisplay = useMemo(() => (data ? data : []), [data]);

  const keys = useMemo(() => getArrayKeys(toDisplay), [toDisplay]);

  const handleOnChangeDateRange = (values: [Date, Date]) => {
    setDateRange(values);
  };

  const colors: Record<string, string> = useMemo(
    () =>
      keys.reduce(
        (acc, key, i) => ({
          ...acc,
          [key]: COLORS[i],
        }),
        {},
      ),
    [keys],
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  const tableBounds = useMemo(() => {
    if (tableData) {
      return getMinMax(tableData[tablePeriod], ["cal1", "cal2", "cal3"]);
    }

    return { min: { value: 0 }, max: { value: 0 } };
  }, [tableData, tablePeriod]);

  const columns: ColumnType<MarketDifferenceTableType>[] = useMemo(() => {
    const firstHeaders: ColumnType<MarketDifferenceTableType>[] = [
      {
        header: () => {},
        accessorKey: "year",
        cell: (cell) => {
          const values = cell.row.original;

          const display =
            tablePeriod === "QUARTER" || tablePeriod === "MONTH"
              ? [values.quarter, values.month]
              : [values.month, values.day];

          return (
            <Flexbox className="gap-0.5 whitespace-nowrap">
              <span className="text-xs font-medium text-black">
                {cell.getValue()}
              </span>

              <span className="text-[10px] text-tertiary-600">
                {display
                  .filter((v) => v)
                  .join(tablePeriod === "DAY" ? " " : " - ")}
              </span>
            </Flexbox>
          );
        },
      },
    ];

    return [
      ...firstHeaders,
      {
        header: "Cal +1",
        accessorKey: "cal1",
        cell: (props) => (
          <RenderTableNumberCell tableBounds={tableBounds} reverse {...props} />
        ),
      },
      {
        header: "Cal +2",
        accessorKey: "cal2",
        cell: (props) => (
          <RenderTableNumberCell tableBounds={tableBounds} reverse {...props} />
        ),
      },
      {
        header: "Cal +3",
        accessorKey: "cal3",
        cell: (props) => (
          <RenderTableNumberCell tableBounds={tableBounds} reverse {...props} />
        ),
      },
    ];
  }, [tableBounds, tablePeriod]);

  useEffect(() => {
    handleOnChangeDefaultGraphsTabs(
      selectedGraphTabId,
      handleOnChangeDateRange,
    );
  }, [selectedGraphTabId]);

  useEffect(() => {
    setGraphVisibility(
      keys.map((key, i) => ({
        name: key,
        isVisible: true,
        color: COLORS[i],
      })),
    );
  }, [keys]);

  return (
    <DisplayBox
      title="Ecart entre les calendaires TTF et PEG"
      Icon={LineChartIcon}
      theme="green"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      className="rounded-xl shadow-displayBoxShadow"
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer midHeight>
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <Flexbox fullWidth fullHeight className="gap-2 rounded-3xl">
            <LineGraph
              data={toDisplay}
              graphVisibility={graphVisibility}
              title="Prix Stlt EEX"
              xAxisDataKey="date"
              colors={colors}
              theme="green"
              keys={keys}
              height={530}
              leftMargin={-10}
              hasDomain={false}
            />
          </Flexbox>
        )}

        <FiltersDisplayBox>
          <GraphDateRangeSelector
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedGraphTabId={selectedGraphTabId}
            setSelectedGraphTabId={setSelectedGraphTabId}
            theme="green"
          />

          <Flexbox fullHeight className="gap-4 overflow-y-auto pb-1">
            <GraphFilter
              title="Légende"
              data={graphVisibility}
              handleOnChange={handleOnChangeGraphVisiblity}
            />

            {daily && !!daily.length && (
              <Flexbox fullWidth className="rounded-lg shadow-displayBoxShadow">
                <Flexbox
                  fullWidth
                  className="border-b-[1px] border-primary-300 bg-primary-200 p-1 text-center text-sm font-medium text-black"
                >
                  <p className="w-full text-center">Spread actuels</p>
                  <p className="w-full text-center text-primary">
                    {format(selectedDate, "dd/MM/yy")}
                  </p>
                </Flexbox>

                <Flexbox className="m-auto px-2 py-1">
                  {daily.map((item) => (
                    <Flexbox
                      key={item.title}
                      row
                      fullWidth
                      align="center"
                      justify="between"
                      className="mb-1 gap-3 text-xs"
                    >
                      <p>{item.title}</p>
                      <p>{item.value} €/MWh</p>
                    </Flexbox>
                  ))}
                </Flexbox>
              </Flexbox>
            )}

            <Flexbox fullWidth className="p-1">
              <TableGraphWidget<MarketDifferenceTableType>
                columns={columns}
                data={tableData ? tableData[tablePeriod] : []}
                tableBounds={tableBounds}
                reverse
                theme="green"
                selectedPeriod={tablePeriod}
                setSelectedPeriod={setTablePeriod}
              />
            </Flexbox>
          </Flexbox>
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default GasMarketDifference;
